/* eslint-disable sonarjs/cognitive-complexity */
import {
  Icons,
  Link,
  SiteSettingsButton,
  Typography
} from '@lacedltd/laced-components'
import * as React from 'react'
import { useLockBodyScroll } from 'react-use'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useLocale, useUser, useTranslation } from 'hooks'
import { withTranslations, withGrowthbook } from 'helpers'
import { NavItem, ShopNavBrand } from '../TopNav.types'
import BeInspiredLinks from '../utils/BeInspiredLinks.json'
import ChristmasLinks from '../utils/ChristmasLinks.json'
import * as S from './MobileNav.styles'
import ExpandedMobileNav from './ExpandedMobileNav'
import AccountMenuSection, { buildAccountSections } from './AccountMenuSection'

const buildAccountLinks = (
  isLoggedIn: boolean,
  isStaff: boolean
): NavItem[] => [
  ...(!isLoggedIn
    ? [
        { title: 'log_in', href: '/users/sign_in' },
        { title: 'sell', href: '/account/selling/new' }
      ]
    : []),
  ...(isStaff ? [{ title: 'laced_admin', href: '/admin' }] : [])
]

const buildChristmasBrand = (locale: string): ShopNavBrand => ({
  title: ChristmasLinks.title,
  url: ChristmasLinks.url,
  topBrand: false,
  subcategories: ChristmasLinks.subcategories.map((sub) => ({
    plp: sub[locale as keyof typeof sub]?.plp ?? '',
    url: sub[locale as keyof typeof sub]?.url ?? ''
  }))
})

const MobileNav: React.FC<MobileNavProps> = ({
  showSiteSettings,
  isOpen,
  shopNav
}) => {
  const { t } = useTranslation()
  const { user, isLoggedIn, fetching } = useUser()
  const { localiseUrl, locale, location, currency } = useLocale()

  const [showMore, setShowMore] = React.useState(false)
  const [selectedBrand, setSelectedBrand] = React.useState<
    ShopNavBrand | undefined
  >(undefined)
  const isOnAccountPage =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/account') ||
      window.location.pathname.startsWith('/users/edit'))

  const defaultToAccount = isLoggedIn && isOnAccountPage

  const [showAccountMenu, setShowAccountMenu] = React.useState(defaultToAccount)

  const christmasContentOn = useFeatureIsOn('christmas_links')

  useLockBodyScroll(isOpen)

  // When user data loads, default to account menu if on account page
  React.useEffect(() => {
    if (defaultToAccount) {
      setShowAccountMenu(true)
    }
  }, [defaultToAccount])

  // Reset to account menu (if on account page) when burger menu closes
  React.useEffect(() => {
    if (!isOpen) {
      setShowAccountMenu(defaultToAccount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (fetching) return

  const userAccountLinks = buildAccountLinks(isLoggedIn, !!user?.isStaff)

  const accountSections = isLoggedIn
    ? buildAccountSections({
        t,
        isSeller: !!user?.seller_profile_complete,
        hasStripeUid: !!user?.stripe_uid
      })
    : []

  const beInspiredSection = [
    ...(location.code === 'GB'
      ? [
          {
            title: 'shared.delivery_methods.premium',
            href: localiseUrl('premium-delivery')
          }
        ]
      : []),
    ...BeInspiredLinks
  ]

  const christmasBrand = buildChristmasBrand(locale)

  const topBrands = shopNav?.filter((b) => b.topBrand) ?? []
  const moreBrands = shopNav?.filter((b) => !b.topBrand) ?? []
  const displayedBrands = showMore ? moreBrands : topBrands

  const handleClick = (brand: ShopNavBrand | undefined) => {
    setSelectedBrand(brand)
  }

  return (
    <S.MobileNav
      // useLockBodyScroll seems to disables all touch scroll events for IOS devices
      // below is a workaround: https://github.com/streamich/react-use/issues/2388#issuecomment-1276899736
      onTouchMove={(e) => e.stopPropagation()}
      data-testid="mobile-nav-menu"
    >
      {showAccountMenu ? (
        <AccountMenuSection
          sections={accountSections}
          onBack={() => setShowAccountMenu(false)}
        />
      ) : selectedBrand || showMore ? (
        <ExpandedMobileNav
          handleClick={handleClick}
          setShowMore={setShowMore}
          selectedBrand={selectedBrand}
          displayedBrands={displayedBrands}
        />
      ) : (
        <>
          <S.ContentSection>
            <S.BrowseAllLink>
              <S.MobileNavLink size="l" href={localiseUrl('/search')}>
                <Typography.Text variant="body">
                  {t('react_components.top_nav.browse_all_items')}
                </Typography.Text>
              </S.MobileNavLink>
            </S.BrowseAllLink>
            <div>
              <S.SectionTitle as="p" variant="body" color="airJordan.p">
                {t('react_components.top_nav.shop')}
              </S.SectionTitle>
              <ul>
                {displayedBrands.map((displayedBrand, i) => (
                  <li key={i}>
                    <S.MobileNavButton
                      fullWidth
                      variant="tertiary"
                      onClick={() => handleClick(displayedBrand)}
                    >
                      <Typography.Text variant="body">
                        {displayedBrand.title}
                      </Typography.Text>{' '}
                      <Icons.Right size="m" />
                    </S.MobileNavButton>
                  </li>
                ))}
                <li>
                  <S.MobileNavButton
                    fullWidth
                    variant="tertiary"
                    onClick={() => setShowMore(true)}
                  >
                    <Typography.Text variant="body">
                      {t('react_components.top_nav.more_brands')}
                    </Typography.Text>{' '}
                    <Icons.Right size="m" />
                  </S.MobileNavButton>
                </li>
              </ul>
            </div>

            <div>
              <S.SectionTitle as="p" variant="body" color="airJordan.p">
                {t('react_components.top_nav.be_inspired')}
              </S.SectionTitle>
              {christmasContentOn && (
                <S.MobileNavButton
                  fullWidth
                  variant="tertiary"
                  onClick={() => handleClick(christmasBrand)}
                >
                  <Typography.Text variant="body">
                    {t(ChristmasLinks.title)}
                  </Typography.Text>{' '}
                  <Icons.Right size="m" />
                </S.MobileNavButton>
              )}
              {beInspiredSection.map((data, i) => (
                <S.MobileNavLink key={i} size="l" href={localiseUrl(data.href)}>
                  <Typography.Text variant="body">
                    {t(data.title)}
                  </Typography.Text>
                </S.MobileNavLink>
              ))}
            </div>
          </S.ContentSection>
          <S.AccountSection>
            <S.Links isLoggedIn={isLoggedIn}>
              {showSiteSettings && (
                <SiteSettingsButton
                  code={location.code}
                  countryName={location.name}
                  language={t(`shared.locales.language.${locale}`)}
                  currency={t(`shared.locales.currency.${currency}`)}
                  onClick={() => {
                    window.dispatchEvent(new Event('toggleSiteSettingsModal'))
                  }}
                  compact
                />
              )}

              {user?.first_name ? (
                <Typography.Heading as="h4">
                  <span data-cs-mask>
                    {t('react_components.top_nav.welcome_back', {
                      user: user.first_name
                    })}
                  </span>
                </Typography.Heading>
              ) : null}

              {isLoggedIn && (
                <S.MobileNavButton
                  fullWidth
                  variant="tertiary"
                  onClick={() => setShowAccountMenu(true)}
                >
                  <Typography.Text variant="footnote" color="airJordan.p">
                    {t('react_components.top_nav.my_account')}
                  </Typography.Text>{' '}
                  <Icons.Right size="m" />
                </S.MobileNavButton>
              )}

              {userAccountLinks.map(({ title, href }) => {
                return (
                  <Link key={title} href={localiseUrl(href)} size="m">
                    <Typography.Text variant="footnote" color="airJordan.p">
                      {t(`react_components.top_nav.${title}`)}
                    </Typography.Text>
                  </Link>
                )
              })}

              {isLoggedIn && (
                <Link href={localiseUrl('/users/sign_out')} size="m">
                  <Typography.Text variant="footnote" color="airJordan.p">
                    {t('react_components.top_nav.logout')}
                  </Typography.Text>
                </Link>
              )}
            </S.Links>
          </S.AccountSection>
        </>
      )}
    </S.MobileNav>
  )
}

interface MobileNavProps {
  showSiteSettings: boolean
  isOpen?: boolean
  shopNav?: ShopNavBrand[]
}

export default withGrowthbook(withTranslations(MobileNav))
